import { useEffect, useRef, useState } from "react";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useCheckoutPageRedirection from "@hooks/useCheckoutPageRedirection";
import useEnrolPageProps from "@hooks/useEnrolPageProps";
import useResetAtomicState from "@hooks/useResetAtomicState";
import useSessionStateSync from "@hooks/useSessionStateSync";

import { getTrackingId } from "@/utils/utils";
import { useAtom, useAtomValue } from "jotai";

import { Flex } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { StateSlicesEnum } from "@ts/enums/StateSlicesEnum";
import { CheckoutStateInterface } from "@ts/interfaces/Misc/state/CheckoutStateInterface";

import {
    checkoutAtom,
    createChargeAtom,
    createSubscriptionAtom,
    isNewsletterSubEnabledAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom";

import { dispatchHSEvents } from "@utils/dispatchHSEevents";

import ChargePurchaseSuccess from "./components/ChargePurchaseSuccess";
import { CheckoutDetailsContainer } from "./containers/CheckoutDetailsContainer";
import { PaymentCaptureContainer } from "./containers/PaymentCaptureContainer";

const CheckoutContainer = () => {
    const pageRedirect = useCheckoutPageRedirection();

    const { hubspotEventCheckout, addonTextbookLink, parentUser } =
        useCheckoutPageProps();
    const { hubspotEventEnrol } = useEnrolPageProps();

    const createSubscriptionState = useAtomValue(createSubscriptionAtom);
    const createChargeState = useAtomValue(createChargeAtom);
    const [checkout] = useAtom(checkoutAtom);
    const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);
    const isNewsletterSubEnabled = useAtomValue(isNewsletterSubEnabledAtom);

    const [isHundredPercentOff, setIsHundredPercentOff] = useState(false);

    const [syncCheckoutSessionState, sessionState] =
        useSessionStateSync<CheckoutStateInterface>();

    const onApplyHundredPercentOff = (state: boolean) => {
        setIsHundredPercentOff(state);
    };

    const { resetCheckoutAtomicState } = useResetAtomicState();

    const isParentUserLoggedIn = !!parentUser;

    const successPageDataRef = useRef<{
        entitlementsNames: string[];
        isBulkAddon: boolean;
    }>({
        entitlementsNames: [],
        isBulkAddon: false
    });

    useEffect(() => {
        const handleRedirectionAfterSubscription = async () => {
            dispatchHSEvents(
                "trackPageView",
                CheckoutPageRoutesEnum.PAYMENT_SUCCESS_PAGE,
                {
                    checkout_status: "PAID",
                    email: checkout.user.email,
                    checkout_token: userCheckoutStateToken
                },
                "checkout",
                hubspotEventCheckout,
                hubspotEventEnrol
            );

            dispatchHSEvents(
                "trackCustomBehavioralEvent",
                CheckoutPageRoutesEnum.PAYMENT_SUCCESS_PAGE,
                {
                    checkout_status: "PAID",
                    checkout_token: userCheckoutStateToken,
                    email: checkout.user.email,
                    newsletter_subscription: isNewsletterSubEnabled
                        ? "yes"
                        : "no"
                },
                "checkout",
                hubspotEventCheckout,
                hubspotEventEnrol
            );

            // If the checkout app was accessed from a place where it expects a redirection back, upon checkout completion
            if (isParentUserLoggedIn) {
                try {
                    await syncCheckoutSessionState(StateSlicesEnum.CHECKOUT, {
                        hasPendingSubCreated:
                            !!sessionState.checkout?.checkoutRedirectsTo
                    });
                } catch (error) {
                    console.log(error);
                }

                resetCheckoutAtomicState();

                // redirect only after cleaning up previous atoms
                window.location.href =
                    sessionState.checkout?.checkoutRedirectsTo ??
                    route("parents.dashboard");
                history.replaceState({}, document.title, route("checkout"));
            } else {
                window.location.href = route("external.ecom.payment-success", {
                    firstName: checkout.user.firstName,
                    email: checkout.user.email
                });
                history.replaceState({}, document.title, route("checkout"));
            }
        };

        const handleRedirectionAfterCharge = async () => {
            await syncCheckoutSessionState(StateSlicesEnum.CHECKOUT, {
                checkoutPurchasingAddon: null,
                addonEntitlementsIds: null
            });

            resetCheckoutAtomicState();
        };

        if (createSubscriptionState.isSuccess) {
            handleRedirectionAfterSubscription();
        }

        if (createChargeState.isSuccess) {
            handleRedirectionAfterCharge();
        }
    }, [createSubscriptionState.isSuccess, createChargeState.isSuccess]);

    useEffect(() => {
        if (sessionState.checkout?.checkoutPurchasingAddon && !checkout.addon) {
            pageRedirect();
        }
    }, [checkout]);

    useEffect(() => {
        successPageDataRef.current.entitlementsNames =
            checkout.addon?.entitlementsNames ?? [];
        successPageDataRef.current.isBulkAddon =
            checkout.addon?.isBulkAddon ?? false;
    }, [checkout]);

    if (createChargeState.isSuccess) {
        const serviceName = successPageDataRef.current.isBulkAddon
            ? `${successPageDataRef.current.entitlementsNames[0].replace(
                  /Service/g,
                  ""
              )} and ${successPageDataRef.current.entitlementsNames[1]}`
            : successPageDataRef.current.entitlementsNames[0];

        return (
            <ChargePurchaseSuccess
                addonTextbookLink={addonTextbookLink ?? ""}
                redirectsTo={sessionState.checkout?.checkoutRedirectsTo ?? ""}
                serviceName={serviceName}
                isBulkAddon={successPageDataRef.current.isBulkAddon}
            />
        );
    }
    return (
        <Flex direction={["column", null, null, "row"]} minH="full">
            <Flex
                flex={[null, null, "0.5"]}
                className={ClassesEnum.PaymentDetailsContainer}
                data-testid="checkout-details-container"
                data-tracking-id={getTrackingId(
                    [ClassesEnum.PaymentDetailsContainer],
                    "checkoutDetailsContainer"
                )}
            >
                <CheckoutDetailsContainer
                    onApplyHundredPercentOff={onApplyHundredPercentOff}
                />
            </Flex>
            <Flex
                flex={[null, null, "0.5"]}
                pb={4}
                className={ClassesEnum.CardDetailsContainer}
                data-testid="payment-capture-container"
                data-tracking-id={getTrackingId(
                    [ClassesEnum.CardDetailsContainer],
                    "paymentCaptureContainer"
                )}
            >
                <PaymentCaptureContainer
                    isHundredPercentOff={isHundredPercentOff}
                />
            </Flex>
        </Flex>
    );
};

export default CheckoutContainer;
